import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { api } from '@/services/api';

// The circle guide (`circle-nav` → `add-member-btn`, #2385) pointed at the
// People page's Add contact button. Both went with the People address book in
// wave w1023c (Victor, 2026-10-23); the Tasks guide is the one left.
export type GuideStep = 'tasks-nav' | 'request-btn' | null;

interface OnboardingGuideContextType {
  guideStep: GuideStep;
  pendingWizardResume: number | null;
  startGuide: () => void;
  advanceToRequestBtn: () => void;
  completeGuide: () => Promise<void>;
  dismissGuide: () => Promise<void>;
  clearWizardResume: () => void;
}

const OnboardingGuideContext = createContext<OnboardingGuideContextType | null>(null);

export function OnboardingGuideProvider({ children }: { children: ReactNode }) {
  const [guideStep, setGuideStep] = useState<GuideStep>(null);
  const [pendingWizardResume, setPendingWizardResume] = useState<number | null>(null);

  const startGuide = useCallback(() => setGuideStep('tasks-nav'), []);
  const advanceToRequestBtn = useCallback(() => setGuideStep('request-btn'), []);

  const completeGuide = useCallback(async () => {
    setGuideStep(null);
    setPendingWizardResume(4);
    try {
      await api.families.updateOnboarding({ steps: { first_task: { done: true } } });
    } catch {
      // best-effort
    }
  }, []);

  const dismissGuide = useCallback(async () => {
    setGuideStep(null);
    setPendingWizardResume(4);
    try {
      await api.families.updateOnboarding({ steps: { first_task: { skipped: true } } });
    } catch {
      // best-effort
    }
  }, []);

  const clearWizardResume = useCallback(() => setPendingWizardResume(null), []);

  return (
    <OnboardingGuideContext.Provider value={{
      guideStep, pendingWizardResume,
      startGuide, advanceToRequestBtn, completeGuide, dismissGuide,
      clearWizardResume,
    }}>
      {children}
    </OnboardingGuideContext.Provider>
  );
}

export function useOnboardingGuide() {
  const ctx = useContext(OnboardingGuideContext);
  if (!ctx) throw new Error('useOnboardingGuide must be used within OnboardingGuideProvider');
  return ctx;
}
